import { Injectable, inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Contexts } from '@sentry/types';
import { ENVIRONMENT_CONFIG } from '@shared/common/injection-tokens/env-config.token';
import { SENTRY_TAG_IS_LOGGED_IN, SENTRY_TAG_IS_NATIVE_APP } from './constants/sentry-tags.contant';
import { EnvironmentType } from '@shared/common/enums/env-type.enum';
import { SentryLogCaptureExceptionParams, SentryLogCaptureMessageParams } from './types/sentry-log-params.type';
import { NativeAppService } from '@shared/common/services/native-app/native-app.service';

@Injectable({
  providedIn: 'root'
})
export class SentryLogService {
  envConfig = inject(ENVIRONMENT_CONFIG);
  nativeAppService: NativeAppService = inject(NativeAppService);
  constructor() {
    this.setTagIsNativeApp();
  }

  captureMessage({ message, severityLevel, reservationId, event, error }: SentryLogCaptureMessageParams): void {
    if (this.envConfig.environment === EnvironmentType.PRODUCTION) {
      Sentry.captureMessage(this.createMessage(message, error), {
        level: severityLevel,
        contexts: this.createContexts(error),
        tags: {
          reservation_id: reservationId,
          event: event
        }
      });
    }
  }

  captureException({ error, message, reservationId, event }: SentryLogCaptureExceptionParams): void {
    this.captureMessage({
      severityLevel: 'error',
      message,
      error,
      event,
      reservationId
    });
  }

  createMessage(message: string, error: any): string {
    if (error && typeof error === 'object' && error.message) {
      message = `${message}${!message.endsWith('.') ? '.' : ''} ${error.message}`;
    }

    return message;
  }

  createContexts(error: any): Contexts {
    return {
      ...(error && typeof error === 'object'
        ? {
            error: {
              name: error.name,
              message: error.message,
              ...(error.status ? { status: error.status } : {}),
              ...(error.statusText ? { statusText: error.statusText } : {}),
              ...(error.url ? { url: error.url } : {}),
              ...(error.error ? { error: JSON.stringify(error.error) } : {})
            }
          }
        : {})
    };
  }

  /**
   * Updates the user `id` context info for future events.
   * @param id It represents the user id string
   */
  setUserId(id: string) {
    Sentry.setUser({ id });
  }

  /**
   * Set the `is_logged_in` tag that will be sent with events.
   * This determines whether a user is logged in or not.
   * @param isLoggedIn Indicates whether the user is logged in
   */
  setTagIsLoggedIn(isLoggedIn: boolean) {
    Sentry.setTag(SENTRY_TAG_IS_LOGGED_IN, isLoggedIn);
  }

  /**
   * Set the `is_native_app` tag that will be sent with events.
   * This determines whether the page is being used inside a WebView.
   * @param isNativeApp Indicates whether the page is running in a native app (e.g. WebView)
   */
  setTagIsNativeApp() {
    const isNativeApp = this.nativeAppService.isNativeApp();

    Sentry.setTag(SENTRY_TAG_IS_NATIVE_APP, isNativeApp);
  }
}
